import { useState } from 'react';
import { isUncertainFailure, useOperationKey } from './walletShared';
import { useTranslation } from 'react-i18next';

export interface LedgerOperation {
  value: string;
  label: string;
}

/** Operation radio + amount + note + save, shared by every points / ZIPA tab. */
export function LedgerAdjustForm({
  operations,
  amountLabel,
  step,
  pending,
  error,
  onSubmit,
  keyed,
  recovery,
}: {
  recovery?: { operation: string; amount: string; note: string; operationKey: string };
  operations: LedgerOperation[];
  amountLabel: string;
  step: string;
  pending: boolean;
  error: string;
  onSubmit: (input: {
    operation: string;
    amount: string;
    note: string;
    operationKey?: string;
  }) => void | Promise<unknown>;
  keyed?: { scope: string; reload: () => Promise<unknown> };
}) {
  const { t } = useTranslation('member');
  const [operation, setOperation] = useState(recovery?.operation ?? operations[0]?.value ?? '');
  const [amount, setAmount] = useState(recovery?.amount ?? '');
  const [note, setNote] = useState(recovery?.note ?? '');
  const [retry, setRetry] = useState(false);
  const key = useOperationKey(
    { scope: keyed?.scope, operation, amount, note },
    keyed?.reload ?? (() => Promise.resolve()),
  );
  const valid = Number(amount) > 0 && note.trim().length > 0;
  return (
    <form
      className="rounded-lg border border-base-300 p-4 space-y-3"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!valid || pending || (keyed && !key.ready)) return;
        try {
          await onSubmit({
            operation,
            amount: amount.trim(),
            note: note.trim(),
            ...(keyed ? { operationKey: recovery?.operationKey ?? key.key } : {}),
          });
          if (keyed) key.renew();
          setRetry(false);
          setAmount('');
          setNote('');
        } catch (failure) {
          if (keyed) {
            key.failed(failure);
            setRetry(isUncertainFailure(failure));
          }
        }
      }}
    >
      <fieldset disabled={pending || !!recovery} className="flex gap-4">
        <legend className="sr-only">{t('ledgerOperation')}</legend>
        {operations.map((op) => (
          <label key={op.value} className="min-h-11 flex gap-2 items-center">
            <input
              type="radio"
              name="ledger-operation"
              value={op.value}
              checked={operation === op.value}
              onChange={() => setOperation(op.value)}
            />
            {op.label}
          </label>
        ))}
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          {amountLabel}
          <input
            disabled={pending || !!recovery}
            type="number"
            min="0"
            step={step}
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className="block w-full min-h-11 rounded border border-base-300 bg-base-100 px-3"
          />
        </label>
        <label className="block">
          {t('ledgerNote')}
          <input
            disabled={pending || !!recovery}
            type="text"
            maxLength={255}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className="block w-full min-h-11 rounded border border-base-300 bg-base-100 px-3"
          />
        </label>
      </div>
      {error && (
        <p role="alert" className="text-sm text-error">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={!valid || pending || (!!keyed && !key.ready)}
        className="min-h-11 rounded bg-primary text-primary-content px-4"
      >
        {pending ? t('ledgerSaving') : t(retry || recovery ? 'ledgerRetry' : 'ledgerSave')}
      </button>
    </form>
  );
}
